/**
 * Alpine.js Components for Admin Bar
 *
 * Provides the interactive functionality for the admin bar panel.
 */

import type { ConditionEvaluationResult } from '../shared/types/targeting';
import { createLogger } from '../shared/logger';

const logger = createLogger('AdminBar');

// ============================================================================
// SVG Icon Imports (Heroicons 2)
// ============================================================================

// Import SVG files as strings via webpack
import checkIcon from './icons/check.svg';
import xMarkIcon from './icons/x-mark.svg';
import clockIcon from './icons/clock.svg';
import arrowDownIcon from './icons/arrow-down.svg';
import arrowRightOnRectangleIcon from './icons/arrow-right-on-rectangle.svg';
import cursorArrowRaysIcon from './icons/cursor-arrow-rays.svg';
import boltIcon from './icons/bolt.svg';
import codeBracketIcon from './icons/code-bracket.svg';
import noSymbolIcon from './icons/no-symbol.svg';
import playIcon from './icons/play.svg';
import pauseIcon from './icons/pause.svg';
import circleOutlineIcon from './icons/circle-outline.svg';
import circleHalfIcon from './icons/circle-half.svg';
import circleFilledIcon from './icons/circle-filled.svg';
import hourglassIcon from './icons/hourglass.svg';
import pencilIcon from './icons/pencil.svg';
import trashIcon from './icons/trash.svg';
import chevronUpIcon from './icons/chevron-up.svg';
import chevronDownIcon from './icons/chevron-down.svg';
import cookieIcon from './icons/cookie.svg';
import stopIcon from './icons/stop.svg';
import hashtagIcon from './icons/hashtag.svg';

/**
 * SVG icons for use in Alpine.js templates.
 * These are imported from SVG files and rendered as inline HTML strings.
 */
const ICONS = {
	check: checkIcon,
	x: xMarkIcon,
	clock: clockIcon,
	timer: clockIcon, // Use clock for timer as well
	scroll: arrowDownIcon,
	exitIntent: arrowRightOnRectangleIcon,
	button: cursorArrowRaysIcon,
	bolt: boltIcon,
	code: codeBracketIcon,
	noSymbol: noSymbolIcon,
	circleEmpty: circleOutlineIcon,
	circleHalf: circleHalfIcon,
	circleFull: circleFilledIcon,
	play: playIcon,
	pause: pauseIcon,
	hourglass: hourglassIcon,
	pencil: pencilIcon,
	trash: trashIcon,
	chevronUp: chevronUpIcon,
	chevronDown: chevronDownIcon,
	cookie: cookieIcon,
	stop: stopIcon,
	hashtag: hashtagIcon,
} as const;

import {
	conditionRegistry,
	getSharedRandomValue,
	setSharedRandomValue,
	resetSharedRandomValue,
	getVisitorNumberCookieName,
} from '../shared/targeting';
import type {
	TriggerState,
	TriggerProgress,
	PopupLifecycleState,
} from '../frontend/triggers/trigger-state-broadcaster';
import type { TriggerType } from '../shared/types/popup';
import { deleteCookie } from '../frontend/utils/cookies';

const PANEL_WIDTH_KEY = 'gp-popups-debug-panel-width';
const PANEL_OPEN_KEY = 'gp-popups-debug-panel-open';
const MIN_PANEL_WIDTH = 340;
const MAX_PANEL_WIDTH = 800;
const DEFAULT_PANEL_WIDTH = 360;

/**
 * Check if the panel was previously open (from localStorage).
 */
export function getInitialPanelOpenState(): boolean {
	try {
		return localStorage.getItem(PANEL_OPEN_KEY) === '1';
	} catch {
		return false;
	}
}

/**
 * Save the panel open state to localStorage.
 */
export function savePanelOpenState(isOpen: boolean): void {
	try {
		if (isOpen) {
			localStorage.setItem(PANEL_OPEN_KEY, '1');
		} else {
			localStorage.removeItem(PANEL_OPEN_KEY);
		}
	} catch {
		// Ignore storage errors
	}
}

/**
 * Create the Alpine data component for the admin bar panel
 */
export function createAdminBarComponent() {
	return {
		/**
		 * Track which feeds have expanded details
		 */
		expanded: {} as Record<number, boolean>,

		/**
		 * Panel width for resizing
		 */
		panelWidth: DEFAULT_PANEL_WIDTH,

		/**
		 * Whether we're currently resizing
		 */
		isResizing: false,

		/**
		 * Initialize the component
		 */
		init() {
			// Restore saved panel width
			const savedWidth = localStorage.getItem(PANEL_WIDTH_KEY);
			if (savedWidth) {
				const width = parseInt(savedWidth, 10);
				if (width >= MIN_PANEL_WIDTH && width <= MAX_PANEL_WIDTH) {
					this.panelWidth = width;
				}
			}

			// Check if panel was previously open (from store, which reads from localStorage)
			// @ts-expect-error - Alpine store is available in component context
			const wasOpen = this.$store.gpPopupsAdminBar.panelOpen;

			// If panel was previously open, show it immediately WITHOUT animation
			if (wasOpen) {
				// Apply panel state immediately (no transition class yet)
				this.showPanel();
			}

			// Add the ready class to enable transitions AFTER initial state is applied
			// Use a double requestAnimationFrame to ensure the initial state is painted first
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					document.documentElement.classList.add(
						'gp-popups-debug-panel-ready'
					);
				});
			});

			// Watch for panel open/close changes and update page margin
			// @ts-expect-error - Alpine's $watch is available in component context
			this.$watch(
				'$store.gpPopupsAdminBar.panelOpen',
				(isOpen: boolean) => {
					if (isOpen) {
						this.showPanel();
					} else {
						this.hidePanel();
					}
				}
			);
		},

		/**
		 * Apply the panel width to the DOM and CSS variable
		 */
		applyPanelWidth() {
			const panel = document.getElementById('gp-popups-debug-panel');
			if (panel) {
				panel.style.width = `${this.panelWidth}px`;
			}
			// Update CSS variable for page margin
			document.documentElement.style.setProperty(
				'--gp-popups-debug-panel-width',
				`${this.panelWidth}px`
			);
		},

		/**
		 * Show the panel and add page margin
		 */
		showPanel() {
			document.documentElement.classList.add(
				'gp-popups-debug-panel-open'
			);
			this.applyPanelWidth();
		},

		/**
		 * Hide the panel and remove page margin
		 */
		hidePanel() {
			document.documentElement.classList.remove(
				'gp-popups-debug-panel-open'
			);
		},

		/**
		 * Start resizing the panel
		 */
		startResize(event: MouseEvent) {
			event.preventDefault();
			this.isResizing = true;

			const panel = document.getElementById('gp-popups-debug-panel');
			const resizeHandle = event.target as HTMLElement;
			resizeHandle.classList.add('resizing');

			const startX = event.clientX;
			const startWidth = this.panelWidth;

			const onMouseMove = (e: MouseEvent) => {
				// Calculate new width (dragging left increases width)
				const deltaX = startX - e.clientX;
				let newWidth = startWidth + deltaX;

				// Clamp to min/max
				newWidth = Math.max(
					MIN_PANEL_WIDTH,
					Math.min(MAX_PANEL_WIDTH, newWidth)
				);

				this.panelWidth = newWidth;
				if (panel) {
					panel.style.width = `${newWidth}px`;
				}
				// Update CSS variable for page margin in real-time
				document.documentElement.style.setProperty(
					'--gp-popups-debug-panel-width',
					`${newWidth}px`
				);
			};

			const onMouseUp = () => {
				this.isResizing = false;
				resizeHandle.classList.remove('resizing');

				// Save width to localStorage
				localStorage.setItem(PANEL_WIDTH_KEY, String(this.panelWidth));

				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
			};

			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
		},

		/**
		 * Toggle the expanded state of a feed's details
		 */
		toggle(feedId: number) {
			this.expanded[feedId] = !this.expanded[feedId];
		},

		/**
		 * Check if a feed's details are expanded
		 */
		isExpanded(feedId: number): boolean {
			return this.expanded[feedId] ?? false;
		},

		/**
		 * Force launch a popup, bypassing dismissal cookie
		 */
		forceLaunch(feedId: number) {
			const gpPopups = this.getGPPopupsAPI();
			if (gpPopups?.forceLaunch) {
				gpPopups.forceLaunch(feedId);
			} else {
				this.warnNoAPI();
			}
		},

		/**
		 * Hide a popup
		 */
		hidePopup(feedId: number) {
			const gpPopups = this.getGPPopupsAPI();
			if (gpPopups?.hide) {
				gpPopups.hide(feedId);
			} else {
				this.warnNoAPI();
			}
		},

		/**
		 * Toggle a popup - launch if closed, hide if open
		 * Uses the store's popupState to determine current state
		 */
		togglePopup(feedId: number) {
			// @ts-expect-error - Alpine store is available in component context
			const feed = this.$store.gpPopupsAdminBar.getFeed(feedId);
			const isOpen = feed?.popupState === 'open';

			if (isOpen) {
				this.hidePopup(feedId);
			} else {
				this.forceLaunch(feedId);
			}
		},

		/**
		 * Get the GPPopups API from window
		 */
		getGPPopupsAPI() {
			return (
				window as Window & {
					GPPopups?: {
						show: (
							feedId: number,
							options?: { bypassDismissal?: boolean }
						) => void;
						hide: (feedId: number) => void;
						toggle: (feedId: number) => void;
						isOpen: (feedId: number) => boolean;
						forceLaunch: (feedId: number) => void;
						testTrigger: (feedId: number) => void;
					};
				}
			).GPPopups;
		},

		/**
		 * Warn when GPPopups API is not available
		 */
		warnNoAPI() {
			logger.warn(
				'GPPopups API not available. The frontend popup script may not be loaded on this page. ' +
					'This can happen if no popups are configured to display on this page.'
			);
		},

		/**
		 * Get the CSS class for a condition result status
		 */
		getStatusClass(result: ConditionEvaluationResult): string {
			if (result.skipped) {
				return 'pending';
			}
			return result.matched ? 'pass' : 'fail';
		},

		/**
		 * Get the icon for a condition result status
		 */
		getStatusIcon(result: ConditionEvaluationResult): string {
			if (result.skipped) {
				return ICONS.hourglass;
			}
			return result.matched ? ICONS.check : ICONS.x;
		},

		/**
		 * Format a condition type for display using the registry
		 */
		formatConditionType(type: string): string {
			const definition = conditionRegistry.get(type);
			if (definition?.label) {
				return definition.label;
			}
			// Fallback: convert snake_case to Title Case
			return type
				.split('_')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ');
		},

		/**
		 * Get a human-readable label for the logic operator
		 */
		formatLogic(logic: string): string {
			return logic === 'AND' ? 'all must match' : 'any must match';
		},

		// ====================================================================
		// Trigger State Helpers
		// ====================================================================

		/**
		 * Get the icon for a trigger type
		 */
		getTriggerIcon(triggerType: TriggerType | undefined): string {
			switch (triggerType) {
				case 'page_load':
					return ICONS.play;
				case 'timeout':
					return ICONS.timer;
				case 'scroll_depth':
					return ICONS.scroll;
				case 'exit_intent':
					return ICONS.exitIntent;
				case 'button':
					return ICONS.button;
				case 'hash':
					return ICONS.hashtag;
				case 'js_api':
					return ICONS.code;
				default:
					return ICONS.bolt;
			}
		},

		/**
		 * Get a human-readable label for a trigger type
		 */
		getTriggerLabel(triggerType: TriggerType | undefined): string {
			switch (triggerType) {
				case 'page_load':
					return 'On Page Load';
				case 'timeout':
					return 'Timeout';
				case 'scroll_depth':
					return 'Scroll Depth';
				case 'exit_intent':
					return 'Exit Intent';
				case 'button':
					return 'Button Click';
				case 'hash':
					return 'Hash';
				case 'js_api':
					return 'JavaScript API';
				default:
					return 'Unknown';
			}
		},

		/**
		 * Format remaining time in a human-readable way
		 */
		formatTimeRemaining(ms: number | undefined): string {
			if (ms === undefined || ms <= 0) {
				return 'Triggered!';
			}
			const seconds = ms / 1000;
			if (seconds >= 1) {
				return `${seconds.toFixed(1)}s`;
			}
			return `${Math.round(ms)}ms`;
		},

		/**
		 * Format scroll progress for display
		 */
		formatScrollProgress(progress: TriggerProgress | undefined): string {
			if (!progress) {
				return '';
			}
			const current = progress.currentValue ?? 0;
			const target = progress.targetValue ?? 0;
			const unit = progress.unit ?? 'percent';

			if (unit === 'percent') {
				return `${Math.round(current)}% / ${target}%`;
			}
			return `${Math.round(current)}px / ${target}px`;
		},

		/**
		 * Get the progress percentage for timeout triggers (0-100)
		 */
		getTimeoutProgress(state: TriggerState | null): number {
			if (!state?.progress?.totalMs || !state?.progress?.remainingMs) {
				return 100;
			}
			const elapsed = state.progress.totalMs - state.progress.remainingMs;
			return Math.min(100, (elapsed / state.progress.totalMs) * 100);
		},

		/**
		 * Get the progress percentage for scroll triggers (0-100)
		 */
		getScrollProgress(state: TriggerState | null): number {
			if (!state?.progress?.targetValue) {
				return 0;
			}
			const current = state.progress.currentValue ?? 0;
			return Math.min(100, (current / state.progress.targetValue) * 100);
		},

		/**
		 * Get the unified progress percentage for any trigger type (0-100)
		 * Used by the template for progress bar width
		 */
		getTriggerProgress(state: TriggerState | null): number {
			if (!state) {
				return 0;
			}

			if (state.triggerType === 'timeout') {
				return this.getTimeoutProgress(state);
			}

			if (state.triggerType === 'scroll_depth') {
				return this.getScrollProgress(state);
			}

			// For other trigger types, return 100 if triggered, 0 otherwise
			return state.state === 'triggered' ? 100 : 0;
		},

		/**
		 * Get the progress text for display in the progress bar
		 */
		getTriggerProgressText(state: TriggerState | null): string {
			if (!state) {
				return '';
			}

			if (state.triggerType === 'timeout') {
				return this.formatTimeRemaining(state.progress?.remainingMs);
			}

			if (state.triggerType === 'scroll_depth') {
				return this.formatScrollProgress(state.progress);
			}

			return state.state === 'triggered' ? 'Triggered!' : 'Waiting...';
		},

		// ====================================================================
		// Popup State Helpers
		// ====================================================================

		/**
		 * Get the icon for a popup lifecycle state
		 */
		getPopupStateIcon(state: PopupLifecycleState | undefined): string {
			switch (state) {
				case 'skipped':
					return ICONS.noSymbol;
				case 'waiting':
					return ICONS.circleEmpty;
				case 'preloading':
					return ICONS.circleHalf;
				case 'preloaded':
					return ICONS.circleHalf;
				case 'triggered':
					return ICONS.circleFull;
				case 'open':
					return ICONS.play;
				case 'dismissed':
					return ICONS.x;
				case 'submitted':
					return ICONS.check;
				default:
					return ICONS.circleEmpty;
			}
		},

		/**
		 * Get a human-readable label for a popup lifecycle state
		 */
		getPopupStateLabel(state: PopupLifecycleState | undefined): string {
			switch (state) {
				case 'skipped':
					return 'Skipped';
				case 'waiting':
					return 'Waiting';
				case 'preloading':
					return 'Preloading...';
				case 'preloaded':
					return 'Preloaded';
				case 'triggered':
					return 'Triggered';
				case 'open':
					return 'Open';
				case 'dismissed':
					return 'Dismissed';
				case 'submitted':
					return 'Submitted';
				default:
					return 'Unknown';
			}
		},

		/**
		 * Get the CSS class for a popup lifecycle state
		 */
		getPopupStateClass(state: PopupLifecycleState | undefined): string {
			switch (state) {
				case 'skipped':
				case 'dismissed':
					return 'state--inactive';
				case 'waiting':
				case 'preloading':
					return 'state--pending';
				case 'preloaded':
					return 'state--ready';
				case 'triggered':
				case 'open':
				case 'submitted':
					return 'state--active';
				default:
					return 'state--pending';
			}
		},

		/**
		 * Check if a popup is currently open based on its state
		 */
		isPopupOpen(state: PopupLifecycleState | undefined): boolean {
			return state === 'open';
		},

		// ====================================================================
		// Trigger Status Helpers
		// ====================================================================

		/**
		 * Trigger display status - determines what to show in the trigger section.
		 * Returns: 'active' | 'dismissed' | 'not-matching' | 'inactive' | 'testing' | 'none'
		 */
		getTriggerDisplayStatus(
			feed: {
				isActive: boolean;
				overallMatch: boolean;
				isDismissedByCookie: boolean;
				isTestingTrigger: boolean;
				triggerState: TriggerState | null;
			} | null
		):
			| 'active'
			| 'dismissed'
			| 'not-matching'
			| 'inactive'
			| 'testing'
			| 'none' {
			if (!feed?.triggerState) {
				return 'none';
			}

			// Testing mode takes priority (for inactive feeds being tested)
			if (feed.isTestingTrigger) {
				return 'testing';
			}

			// Dismissed by cookie
			if (feed.isDismissedByCookie) {
				return 'dismissed';
			}

			// Conditions not met
			if (!feed.overallMatch) {
				return 'not-matching';
			}

			// Feed is inactive
			if (!feed.isActive) {
				return 'inactive';
			}

			// Active and ready to trigger
			return 'active';
		},

		/**
		 * Get the disabled reason text for a trigger
		 */
		getTriggerDisabledReason(
			status: 'dismissed' | 'not-matching' | 'inactive'
		): string {
			switch (status) {
				case 'dismissed':
					return 'Trigger disabled; previously dismissed.';
				case 'not-matching':
					return 'Trigger disabled; conditions not met.';
				case 'inactive':
					return 'Trigger disabled; feed inactive.';
			}
		},

		/**
		 * Get the icon for a disabled trigger status
		 */
		getTriggerDisabledIcon(
			status: 'dismissed' | 'not-matching' | 'inactive'
		): string {
			return status === 'inactive' ? ICONS.pause : ICONS.noSymbol;
		},

		/**
		 * Check if trigger has progress (timeout or scroll_depth)
		 */
		triggerHasProgress(state: TriggerState | null): boolean {
			if (!state) {
				return false;
			}
			return (
				state.triggerType === 'timeout' ||
				state.triggerType === 'scroll_depth'
			);
		},

		/**
		 * Check if popup state badge should be shown.
		 * Only show when trigger is active/testing or popup has meaningful state.
		 */
		shouldShowPopupState(
			feed: {
				isActive: boolean;
				overallMatch: boolean;
				isDismissedByCookie: boolean;
				isTestingTrigger: boolean;
				popupState: PopupLifecycleState;
			} | null
		): boolean {
			if (!feed?.popupState) {
				return false;
			}

			// Always show for meaningful terminal states
			if (
				feed.popupState === 'open' ||
				feed.popupState === 'dismissed' ||
				feed.popupState === 'submitted'
			) {
				return true;
			}

			// Show when trigger is actively running
			if (feed.isTestingTrigger) {
				return true;
			}

			// Show when active, matching, and not dismissed
			if (
				feed.isActive &&
				feed.overallMatch &&
				!feed.isDismissedByCookie
			) {
				return true;
			}

			return false;
		},

		// ====================================================================
		// Test Trigger Helpers (for inactive feeds)
		// ====================================================================

		/**
		 * Test a trigger for an inactive feed.
		 * Uses the GPPopups API to initialize and run the actual trigger logic,
		 * avoiding duplication of trigger code.
		 */
		testTrigger(feedId: number): void {
			const gpPopups = this.getGPPopupsAPI();
			if (gpPopups?.testTrigger) {
				// Clear dismissal cookie first so the popup can show
				// @ts-expect-error - Alpine store is available in component context
				const feed = this.$store.gpPopupsAdminBar.getFeed(feedId);
				if (feed?.respectDismissal && feed?.cookieName) {
					this.clearDismissalCookie(feedId, feed.cookieName);
				}

				// Mark as testing in the store
				// @ts-expect-error - Alpine store is available in component context
				this.$store.gpPopupsAdminBar.startTestTrigger(feedId);

				// Use the GPPopups API to initialize and run the trigger
				gpPopups.testTrigger(feedId);
			} else {
				this.warnNoAPI();
			}
		},

		// ====================================================================
		// Traffic Percentage Condition Helpers
		// ====================================================================

		/**
		 * Check if a condition is a traffic percentage condition
		 */
		isTrafficPercentageCondition(conditionType: string): boolean {
			return conditionType === 'traffic_percentage';
		},

		/**
		 * Get the shared random value used for traffic percentage conditions.
		 * This value is consistent across all popups on the page.
		 */
		getTrafficRandomValue(): number {
			return getSharedRandomValue();
		},

		/**
		 * Get detailed traffic percentage info for display.
		 * Returns an object with the random value, range, and explanation.
		 */
		getTrafficPercentageInfo(condition: ConditionEvaluationResult): {
			randomValue: number;
			min: number;
			max: number;
			matched: boolean;
			rangeText: string;
			explanation: string;
		} {
			const randomValue = getSharedRandomValue();
			// Extract range from the reason string
			// The reason format is: "Visitor #X is within/outside range Y-Z"
			const rangeMatch = condition.reason?.match(/range (\d+)-(\d+)/);
			const min = rangeMatch ? parseInt(rangeMatch[1], 10) : 1;
			const max = rangeMatch ? parseInt(rangeMatch[2], 10) : 100;
			const percentage = max - min + 1;

			return {
				randomValue,
				min,
				max,
				matched: condition.matched,
				rangeText: `${min}–${max}`,
				explanation: condition.matched
					? `Visitor #${randomValue} is in range ${min}–${max} (${percentage}%)`
					: `Visitor #${randomValue} is outside range ${min}–${max} (${percentage}%)`,
			};
		},

		/**
		 * Format traffic percentage condition for rich display
		 */
		formatTrafficPercentageReason(
			condition: ConditionEvaluationResult
		): string {
			const info = this.getTrafficPercentageInfo(condition);
			return info.explanation;
		},

		// ====================================================================
		// Cookie Management Helpers
		// ====================================================================

		/**
		 * Clear a specific popup's dismissal cookie, update the store, and restart the trigger.
		 * This allows the popup to show again after being dismissed.
		 */
		clearDismissalCookie(feedId: number, cookieName: string): void {
			deleteCookie(cookieName);
			// Update the store to reflect the change (reactive)
			// @ts-expect-error - Alpine store is available in component context
			this.$store.gpPopupsAdminBar.clearDismissedStatus(feedId);

			// Restart the trigger so the popup can show again
			const gpPopups = this.getGPPopupsAPI();
			if (gpPopups?.testTrigger) {
				// @ts-expect-error - Alpine store is available in component context
				const feed = this.$store.gpPopupsAdminBar.getFeed(feedId);
				// Only restart trigger for active feeds that match targeting
				if (feed?.isActive && feed?.overallMatch) {
					gpPopups.testTrigger(feedId);
				}
			}
		},

		/**
		 * Clear all GP Popups dismissal cookies and update the store.
		 * Iterates through all feeds and clears each feed's specific cookie name,
		 * supporting both default (gp_popup_{feedId}) and custom cookie names.
		 */
		clearAllDismissalCookies(): void {
			// @ts-expect-error - Alpine store is available in component context
			const feeds = this.$store.gpPopupsAdminBar.feeds;

			// Clear each feed's dismissal cookie by its specific cookie name
			for (const feed of feeds) {
				if (feed.respectDismissal && feed.cookieName) {
					deleteCookie(feed.cookieName);
				}
			}

			// Update the store to reflect the change (reactive)
			// @ts-expect-error - Alpine store is available in component context
			this.$store.gpPopupsAdminBar.clearAllDismissedStatuses();
		},

		// ====================================================================
		// Visitor Number Management (for A/B Testing)
		// ====================================================================

		/**
		 * Get the current visitor number
		 */
		getVisitorNumber(): number {
			return getSharedRandomValue();
		},

		/**
		 * Get the cookie name used for visitor number
		 */
		getVisitorCookieName(): string {
			return getVisitorNumberCookieName();
		},

		/**
		 * Set a specific visitor number (for testing different A/B variants)
		 */
		setVisitorNumber(value: number): void {
			if (value >= 1 && value <= 100) {
				setSharedRandomValue(value);
				// Trigger a page reload to re-evaluate targeting with new visitor number
				window.location.reload();
			}
		},

		/**
		 * Reset visitor number to a new random value
		 */
		resetVisitorNumber(): void {
			resetSharedRandomValue();
			// Trigger a page reload to re-evaluate targeting with new visitor number
			window.location.reload();
		},

		/**
		 * Prompt user for a new visitor number
		 */
		promptVisitorNumber(): void {
			const current = getSharedRandomValue();
			// eslint-disable-next-line no-alert
			const input = prompt(
				`Enter a visitor number (1–100) for A/B testing.\nCurrent: ${current}`,
				String(current)
			);
			if (input !== null) {
				const value = parseInt(input, 10);
				if (!isNaN(value) && value >= 1 && value <= 100) {
					this.setVisitorNumber(value);
				} else {
					// eslint-disable-next-line no-alert
					alert('Please enter a number between 1 and 100.');
				}
			}
		},

		// ====================================================================
		// Icon Helpers (for PHP template)
		// ====================================================================

		/**
		 * Get the pencil/edit icon
		 */
		getPencilIcon(): string {
			return ICONS.pencil;
		},

		/**
		 * Get the trash icon
		 */
		getTrashIcon(): string {
			return ICONS.trash;
		},

		/**
		 * Get the cookie icon
		 */
		getCookieIcon(): string {
			return ICONS.cookie;
		},

		/**
		 * Get the chevron icon (up or down)
		 */
		getChevronIcon(expanded: boolean): string {
			return expanded ? ICONS.chevronUp : ICONS.chevronDown;
		},

		/**
		 * Get the play/stop icon for popup toggle
		 */
		getPlayStopIcon(isOpen: boolean): string {
			return isOpen ? ICONS.stop : ICONS.play;
		},

		/**
		 * Get the condition status icon (check, x, or circle)
		 */
		getConditionStatusIcon(matched: boolean, skipped: boolean): string {
			if (skipped) {
				return ICONS.circleEmpty;
			}
			return matched ? ICONS.check : ICONS.x;
		},
	};
}

// ============================================================================
// Global Icon Helper Functions (for PHP template x-html bindings)
// ============================================================================

/**
 * Get the status icon for a condition result
 * Used in PHP template with x-html
 */
export function getStatusIcon(result: {
	matched: boolean;
	skipped?: boolean;
}): string {
	if (result.skipped) {
		return ICONS.hourglass;
	}
	return result.matched ? ICONS.check : ICONS.x;
}

/**
 * Get the pencil/edit icon
 */
export function getPencilIcon(): string {
	return ICONS.pencil;
}

/**
 * Get the trash icon
 */
export function getTrashIcon(): string {
	return ICONS.trash;
}

/**
 * Get the cookie icon
 */
export function getCookieIcon(): string {
	return ICONS.cookie;
}

/**
 * Get the chevron icon (up or down)
 */
export function getChevronIcon(expanded: boolean): string {
	return expanded ? ICONS.chevronUp : ICONS.chevronDown;
}

/**
 * Get the play/stop icon for popup toggle
 */
export function getPlayStopIcon(isOpen: boolean): string {
	return isOpen ? ICONS.stop : ICONS.play;
}

/**
 * Get the condition status icon (check, x, or circle)
 */
export function getConditionStatusIcon(
	matched: boolean,
	skipped: boolean = false
): string {
	if (skipped) {
		return ICONS.circleEmpty;
	}
	return matched ? ICONS.check : ICONS.x;
}
